<template>
  <v-container>
    <help-button>
       <div v-show="!custom" class="hintText">
          <p >
            <b>Use the Conjugator:</b>
          </p>
          <p >1. Select a verb to conjugate from the list. There are four regular forms: 1-Syllable, broad and slender, and 2-Syllable, broad and slender. Irregular verbs are also available for some tenses. Verbs which involve syncope have not been included. 
          </p>
          <p >2. Use the selection buttons to navigate to the desired conjugation of tense and form. Note the difference between the independent and dependent (negative and interrogative) forms.
          </p>
        </div>
        <div v-show="custom" class="hintText">
          <p >
            <b>Use the Custom Conjugator:</b>
          </p>
          <p >1. Choose a regular verb to be conjugated from a Classical Modern Irish glossary or dictionary, such as the <a style="color: white" href="https://xn--lamh-bpa.org/glossary/" target="_blank" rel="noopener noreferrer">Léamh.org Glossary</a>.
          </p>
          <p >2. In the "Custom Headword" text box, enter the 3rd person singular present tense independent form. This is generally the headword of a glossary entry. This form generally ends in -idh. 
          </p>
          <p >3. Select the correct verb category.
          </p>
          <p >4. View your verb paradigms below.</p>
          <p></p>
          <h4>Caveats:</h4>
          <p ><em>Please note that some of the verb forms generated may be theoretical/unattested.</em></p>
          <p ><em>The custom headword feature has not yet been coded to handle verbs that undergo syncope; syncopated verbs can be entered, but may not conjugate correctly.</em></p>
        </div>
    </help-button>
    <v-layout column justify-space-around align-space-around>
      <v-autocomplete
        label="Verb"
        :items="sampleVerbs"
        item-text="verb"
        v-model="dictionaryForm"
        v-show="!custom"
        rounded
        solo
        id="autocomplete"
      >
        <template v-slot:selection="data">
          <template>
            <v-list-tile-title
              v-text="data.item.verb"
              class="title"
            ></v-list-tile-title>
            <v-list-tile-sub-title
              v-text="data.item.cat"
              class="caption"
            ></v-list-tile-sub-title>
          </template>
        </template>
        <template v-slot:item="data">
          <template>
            <v-list-tile-content>
              <v-list-tile-title
                v-text="data.item.verb"
                class="title"
              ></v-list-tile-title>
              <v-list-tile-sub-title
                v-text="data.item.cat"
                class="caption"
              ></v-list-tile-sub-title>
            </v-list-tile-content> </template
        ></template>
      </v-autocomplete>
    </v-layout>
    <v-flex>
      <v-select
        :items="categories"
        v-show="custom"
        label="Category"
        v-model="customCat"
      ></v-select>
      <v-text-field
        v-show="custom"
        label="Custom Headword"
        v-model="customHeadword"
      ></v-text-field>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-checkbox
            label="Custom Headword"
            v-model="custom"
            v-bind="attrs"
            v-on="on"
          ></v-checkbox>
        </template>
        <span>Enter your own verb</span>
      </v-tooltip>
    </v-flex>

    <v-flex mb-1>
      <!-- <v-select :items="tenses" label="Tense/Mood" v-model="selectedTense"></v-select> -->
      <v-layout mb-2>
        <v-btn-toggle group mandatory v-model="selectedTense">
          <div v-for="(value, index) in tenses" :key="(value, index)">
            <template v-if="index == 0">
              <v-btn
                color="#9C9F84"
                v-for="tense in tenses"
                :key="tense"
                :value="tense"
                >{{ tense }}</v-btn
              >
            </template>
          </div>
        </v-btn-toggle>
      </v-layout>
      <v-layout mb-2>
        <v-btn-toggle
          group
          mandatory
          v-model="depFormRadio"
          v-show="showRadioButtons"
        >
          <v-btn color="#668099" label="independent" value="independent"
            >independent</v-btn
          >
          <v-btn color="#668099" label="negative" value="negative"
            >negative</v-btn
          >
          <v-btn color="#668099" label="interrogative" value="interrogative"
            >interrogative</v-btn
          >
        </v-btn-toggle>
      </v-layout>
    </v-flex>
    <v-layout>
      <v-flex>
        <table>
          <thead>
            <tr>
              <th></th>
              <th v-for="num in numbers" :key="num">
                {{num.charAt(0).toUpperCase() + num.slice(1)}}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(per, j) in persons" :key="j">
              <td style="padding: 1em;"><b>{{ per }}</b></td>
              <td v-if="!enumerated"></td>
              <td v-if="!enumerated"></td>
              <td v-if="enumerated">
              <div class = "striped" v-for="x in stripedTable(enumerated[j])" :key="x">
                > {{ x }}
              </div>
              </td>
              <td v-if="enumerated">
                <div class = "striped" v-for="x in stripedTable(enumerated[j+3])" :key="x">
                > {{ x }}
              </div>
              </td>
            </tr>
          </tbody>
        </table>

        <span v-show="showClassicalWarning">
          <sup>*</sup>This is not classical
        </span>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import {
  PARTS_OF_SPEECH,
  GENDER,
  VERB_TENSES,
  VERB_PERSON,
  VERB_NUMBER,
  VERB_CATEGORY,
  SAMPLE_VERBS,
} from "@/constants.js";
import {
  conjugate,
  generateStem,
  enumerateConjugations,
  allFormsOfWord,
} from "@/conjugation.js";
import { VERB_FORMS } from "../constants";
import HelpButton from "../components/HelpButton.vue";
export default {
  components: { HelpButton },
  name: "Conjugate",
  props: {
    word: Object,
  },
  data() {
    return {
      custom: false,
      customCat: null,
      verbStem: "",
      sampleVerbs: SAMPLE_VERBS,
      dictionaryForm: Object.values(SAMPLE_VERBS)[0].verb,
      customHeadword: "",
      selectedTense: Object.values(VERB_TENSES)[0],
      selectedNumber: Object.values(VERB_NUMBER)[0],
      selectedCategory: Object.values(VERB_CATEGORY)[0],
      selectedPerson: Object.values(VERB_PERSON)[0],
      depForms: Object.values(VERB_FORMS),
      selectedDepForm: Object.values(VERB_FORMS)[0],
      categories: Object.values(VERB_CATEGORY),
      numbers: Object.values(VERB_NUMBER),
      persons: Object.values(VERB_PERSON),
      tenses: Object.values(VERB_TENSES),
      words: Object.values(SAMPLE_VERBS),
      depFormRadio: VERB_FORMS.independent,
    };
  },
  computed: {
    selectedVerbCategory() {
      if (this.dictionaryForm) {
        if (!this.custom) {
          var currentWord = this.sampleVerbs.find(
            (w) => w.verb === this.dictionaryForm
          );
          return currentWord.cat ? currentWord.cat : this.categories[0];
        } else {
          return this.customCat;
        }
      }
    },
    stem() {
      var headword = this.custom ? this.customHeadword : this.dictionaryForm;
      return generateStem(
        headword,
        this.selectedVerbCategory,
        this.selectedTense
      );
    },
    enumerated() {
      if (this.stem.length > 0) {
        return enumerateConjugations(
          this.selectedVerbCategory,
          this.selectedTense,
          this.depFormRadio,
          this.stem
        );
      }
      else{
        console.error("Could not generate stem for given verb");
      }
    },
    showRadioButtons() {
      if (this.selectedTense === VERB_TENSES.subjunctive) {
        this.depFormRadio = VERB_FORMS.independent;
        return false;
      } else {
        return true;
      }
    },
    showInterrogative(){
      if (this.selectedTense === VERB_TENSES.imperative){
        this.depFormRadio = VERB_FORMS.independent;
        return false;
      } else {
        return true;
      }
    },
    showClassicalWarning() {
      return (
        this.selectedTense === VERB_TENSES.future ||
        this.selectedTense === VERB_TENSES.present
      );
    },
  },
  methods: {
    expandWord: function (word) {
      // var wordString = allFormsOfWord(word).join(", ");
      // return wordString;
      return allFormsOfWord(word);
    },
    stripedTable: function(enumerated) { // do all the for loops in here to make striping simple
      var words = [];
      for(var j = 0; j < enumerated.length; j++){
        words.push(this.expandWord(enumerated[j]));
      }
      var merged = [].concat.apply([], words);
      return merged;
    }
  },
};
</script>

<style scoped>
.incorrect {
  color: red;
}
table {
    border: 2px solid #415370;
    border-collapse: separate;
    border-left: 0;
    border-radius: 4px;
    border-spacing: 0px;
    color: #415370;
}
thead {
    display: table-header-group;
    vertical-align: middle;
    border-color: inherit;
    border-collapse: separate;
}
tr {
    display: table-row;
    vertical-align: inherit;
    border-color: inherit;
}
th, td {
    padding: 1em 0;
    vertical-align: top;
    border-left: 2px solid #415370;    
}
td {
    border-top: 2px solid #415370;    
}
thead:first-child tr:first-child th:first-child, tbody:first-child tr:first-child td:first-child {
    border-radius: 3px 0 0 0;
}
thead:last-child tr:last-child th:first-child, tbody:last-child tr:last-child td:first-child {
    border-radius: 0 0 0 3px;
}
.hintText {
  color: white;
  padding: 20px;
  margin-bottom: 0px !important;
}
.striped{
  padding: 0 1em;
}
.striped:nth-child(odd) {
  background-color: #DCE0E8;
  }
</style>
